var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Cartesian2, Cartesian3, Color, ConstantPositionProperty, HorizontalOrigin, IntersectionTests, Plane, ScreenSpaceEventType, VerticalOrigin, BoundingSphere, Matrix4, } from "cesium";
import { Activity, ActivityLogger } from "../../utilities/activity-logger";
import { translatePoints, createRotationModelMatrix, createTranslationModelMatrix, createScaleModelMatrix, calculateRotationAngleAndDirection, calculateScaleFactor, } from "../../utilities/spatial-transform-util";
import { createDrawing2DImagePrimitive } from "../../site/drawing-2d-Image/utils";
function isEditable() {
    const retVal = (target, key, descriptor) => {
        const original = descriptor.value;
        descriptor.value = function (...args) {
            const drawing2DImageHandler = this;
            if (drawing2DImageHandler.imageUnderEdit) {
                return original.apply(this, args);
            }
            else {
                console.error(`Call start on drawing2DImageHandler before calling ${String(key)}`);
            }
        };
        return descriptor;
    };
    return retVal;
}
export class Drawing2DImageEditHandler {
    _callback;
    _cesiumPicker;
    _originalImage;
    _isVisible = true;
    _imageUnderEdit;
    _onEditInfo;
    _cesiumViewer;
    _site;
    _handler;
    _rotationSphere;
    _scaleSquare;
    _onKeyDownFuncHandler;
    _previousLeftDownAction;
    _supportPlane;
    _preview;
    get imageUnderEdit() {
        return this._originalImage;
    }
    constructor(args, cesiumViewer, site) {
        this._cesiumPicker = args.cesiumPicker;
        this._handler = args.handler;
        this._cesiumViewer = cesiumViewer;
        this._site = site;
        this._preview = args.preview;
        this._rotationSphere = this._cesiumViewer.viewer.entities.add({
            position: new Cartesian3(1000, 1000, 1000),
            billboard: {
                image: this.drawRotationCircle(),
                show: true,
                pixelOffset: new Cartesian2(0, 10),
                eyeOffset: new Cartesian3(0.0, 0.0, 0.0),
                horizontalOrigin: HorizontalOrigin.CENTER,
                verticalOrigin: VerticalOrigin.BOTTOM,
                scale: 2.0,
                color: Color.RED.withAlpha(0.5),
                rotation: 0.0,
                alignedAxis: Cartesian3.ZERO,
                width: 10,
                height: 10,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
        });
        this._rotationSphere.addProperty("type");
        this._rotationSphere["type"] = "rotation-helper";
        this._rotationSphere.show = false;
        this._scaleSquare = this._cesiumViewer.viewer.entities.add({
            position: new Cartesian3(1000, 1000, 1000),
            billboard: {
                image: this.drawScaleSquare(),
                show: true,
                pixelOffset: new Cartesian2(0, 10),
                eyeOffset: new Cartesian3(0.0, 0.0, 0.0),
                horizontalOrigin: HorizontalOrigin.CENTER,
                verticalOrigin: VerticalOrigin.BOTTOM,
                scale: 2.0,
                color: Color.RED.withAlpha(0.5),
                rotation: 0.0,
                alignedAxis: Cartesian3.ZERO,
                width: 10,
                height: 10,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
        });
        this._scaleSquare.addProperty("type");
        this._scaleSquare["type"] = "scale-helper";
        this._scaleSquare.show = false;
        this._onEditInfo = {
            wgs84CornerPositions: [],
            height: 0,
            transparency: 1.0,
        };
    }
    drawScaleSquare() {
        // create and draw an image using a canvas
        const canvas = document.createElement("canvas");
        canvas.width = 150;
        canvas.height = 150;
        const context2D = canvas.getContext("2d");
        // ... draw image
        context2D.fillStyle = "red";
        context2D.fillRect(0, 0, 150, 150);
        return canvas;
    }
    drawRotationCircle() {
        // create and draw an image using a canvas
        const canvas = document.createElement("canvas");
        canvas.width = 150;
        canvas.height = 150;
        const context2D = canvas.getContext("2d");
        // ... draw image
        context2D.fillStyle = "red";
        context2D.beginPath();
        context2D.arc(75, 75, 75, 0, 2 * Math.PI);
        context2D.fill();
        return canvas;
    }
    getRenderedCornerPositions() {
        return this._onEditInfo.wgs84CornerPositions.map((corner) => {
            return Matrix4.multiplyByPoint(this._imageUnderEdit.modelMatrix, corner, new Cartesian3());
        });
    }
    updateEditHelpers() {
        const corners = this.getRenderedCornerPositions();
        const midPoint = Cartesian3.midpoint(corners[0], corners[1], new Cartesian3());
        const center = BoundingSphere.fromPoints(corners).center;
        const vec = Cartesian3.subtract(midPoint, center, new Cartesian3());
        const length = Cartesian3.distance(center, midPoint);
        Cartesian3.normalize(vec, vec);
        const rotationSpherePos = Cartesian3.add(center, Cartesian3.multiplyByScalar(vec, length + 4, new Cartesian3()), new Cartesian3());
        this._rotationSphere.position = new ConstantPositionProperty(rotationSpherePos);
        this._scaleSquare.position = new ConstantPositionProperty(corners[1]);
    }
    async start(image, callback) {
        try {
            this._originalImage = image;
            this._isVisible = image.isVisible();
            this._callback = callback;
            this._originalImage.updateVisibility(false);
            this._onEditInfo.wgs84CornerPositions =
                await this._site.convertPointsToWGS84(this._originalImage.info.localCornerPositions);
            this._onEditInfo.height = this._originalImage.getElevation();
            this._onEditInfo.transparency = this._originalImage.info.transparency;
            this.rerenderImage();
            this.updateEditHelpers();
            const corners = this.getRenderedCornerPositions();
            this._cesiumViewer.viewerCamera.set2DView(corners[0], corners[1], corners[2], corners[3]);
            this._rotationSphere.show = true;
            this._scaleSquare.show = true;
            this._previousLeftDownAction = this._handler.getInputAction(ScreenSpaceEventType.LEFT_DOWN);
            const center = BoundingSphere.fromPoints(this._onEditInfo.wgs84CornerPositions).center;
            this._supportPlane = Plane.fromPointNormal(center, Cartesian3.normalize(center, new Cartesian3()));
            this._handler.setInputAction(this.onLeftDown.bind(this), ScreenSpaceEventType.LEFT_DOWN);
            this._onKeyDownFuncHandler = this.onHandleKeyDown.bind(this);
            document.addEventListener("keydown", this._onKeyDownFuncHandler);
        }
        catch (err) {
            this._callback?.onError && this._callback?.onError(err);
        }
        this._callback?.onStart && this._callback?.onStart("Edit Started");
    }
    async end(args) {
        try {
            const newLocalCornerPositions = await this._site.convertPointsToLocalSpace(this.getRenderedCornerPositions());
            if (!this._originalImage.info.isDraped) {
                newLocalCornerPositions.forEach((corner) => {
                    corner.z = this._onEditInfo.height;
                });
            }
            await this._originalImage.updateCornerPositions(newLocalCornerPositions);
            await this._originalImage.updateHeightMap();
            this._originalImage.render();
            this._originalImage.updateVisibility(this._isVisible);
            this._originalImage.updateTransparency(this._onEditInfo.transparency);
            this.clearAll();
            ActivityLogger.log(Activity.REFERENCE_TOPOLOGY_EDITED);
        }
        catch (err) {
            this._callback?.onError && this._callback?.onError(err);
            return;
        }
        this._callback?.onFinish && this._callback?.onFinish("Edit Ended");
    }
    async cancel() {
        this._originalImage.updateVisibility(this._isVisible);
        this.clearAll();
        this._callback?.onCancel && this._callback?.onCancel();
    }
    /**
     * Synchronizes the image and all edit handles while retaining the edit session.
     */
    setVisibility(visible) {
        if (!this._originalImage || !this._imageUnderEdit)
            return;
        this._isVisible = visible;
        this._imageUnderEdit.show = visible;
        this._rotationSphere.show = visible;
        this._scaleSquare.show = visible;
        this._cesiumViewer.viewer.scene.requestRender();
    }
    updateTransparency(alpha = 1.0) {
        this._imageUnderEdit.appearance.material.uniforms.color.alpha = alpha;
        this._onEditInfo.transparency = alpha;
    }
    async updateElevation(elevation) {
        if (!this._originalImage.info.isDraped) {
            const newLocalCornerPositions = await this._site.convertPointsToLocalSpace(this._onEditInfo.wgs84CornerPositions);
            newLocalCornerPositions.forEach((corner) => {
                corner.z = elevation;
            });
            const newWgs84CornerPositions = await this._site.convertPointsToWGS84(newLocalCornerPositions);
            this._onEditInfo.wgs84CornerPositions = newWgs84CornerPositions;
            this._onEditInfo.height = elevation;
            this.rerenderImage();
            this.updateEditHelpers();
        }
    }
    clearAll() {
        this._cesiumViewer.viewer.scene.screenSpaceCameraController.enableRotate = true;
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
        this._previousLeftDownAction &&
            this._handler.setInputAction(this._previousLeftDownAction, ScreenSpaceEventType.LEFT_DOWN);
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
        document.removeEventListener("keydown", this._onKeyDownFuncHandler);
        this._rotationSphere.show = false;
        this._scaleSquare.show = false;
        this._cesiumViewer.viewerCamera.set3DView();
        this._supportPlane = undefined;
        this._preview.resetSphereColor();
        this._preview.setSphereVisibility(false);
        this._imageUnderEdit &&
            this._cesiumViewer.viewer.scene.primitives.remove(this._imageUnderEdit);
        this._imageUnderEdit = undefined;
        this._originalImage = undefined;
        this._onEditInfo.wgs84CornerPositions.length = 0;
        this._onEditInfo.height = 0;
        this._cesiumViewer.viewer.scene.requestRender();
    }
    async onHandleKeyDown(event) {
        switch (event.key) {
            case "Esc":
            case "Escape":
                await this.cancel();
                break;
            default:
        }
    }
    onLeftDown(e) {
        //check if it clicked on the rotation sphere or sphere or the image
        const pickedEntity = this._cesiumPicker.getRenderedEntity(e.position);
        const leftDownPosition = e.position;
        switch (pickedEntity?.type) {
            case "rotation-helper":
                this._cesiumViewer.viewer.scene.screenSpaceCameraController.enableRotate = false;
                this._handler.setInputAction(this.onMouseMoveToRotate.bind(this, leftDownPosition), ScreenSpaceEventType.MOUSE_MOVE);
                this._handler.setInputAction(this.onLeftUp.bind(this), ScreenSpaceEventType.LEFT_UP);
                this._callback?.onProgress &&
                    this._callback?.onProgress("rotation Started");
                break;
            case "image":
                if (pickedEntity.id === this._originalImage.id) {
                    this._cesiumViewer.viewer.scene.screenSpaceCameraController.enableRotate = false;
                    this._handler.setInputAction(this.onMouseMoveToTranslate.bind(this, leftDownPosition), ScreenSpaceEventType.MOUSE_MOVE);
                    this._handler.setInputAction(this.onLeftUp.bind(this), ScreenSpaceEventType.LEFT_UP);
                    this._callback?.onProgress &&
                        this._callback?.onProgress("translation Started");
                }
                break;
            case "scale-helper":
                this._cesiumViewer.viewer.scene.screenSpaceCameraController.enableRotate = false;
                this._handler.setInputAction(this.onMouseMoveToScale.bind(this), ScreenSpaceEventType.MOUSE_MOVE);
                this._handler.setInputAction(this.onLeftUp.bind(this), ScreenSpaceEventType.LEFT_UP);
                this._callback?.onProgress &&
                    this._callback?.onProgress("scale Started");
                break;
            default:
                break;
        }
    }
    rerenderImage() {
        this._imageUnderEdit &&
            this._cesiumViewer.viewer.scene.primitives.remove(this._imageUnderEdit);
        this._imageUnderEdit = createDrawing2DImagePrimitive({
            file: this._originalImage.info.file,
            assetId: this._originalImage.id,
            wgs84CornerPositions: this._onEditInfo.wgs84CornerPositions,
            isDraped: false,
            isVisible: this._isVisible,
            disableDepthTest: this._originalImage.info.isDraped,
        });
        this._imageUnderEdit.appearance.material.uniforms.color.alpha =
            this._onEditInfo.transparency;
        this._cesiumViewer.viewer.scene.primitives.add(this._imageUnderEdit);
    }
    onLeftUp(e) {
        this._onEditInfo.wgs84CornerPositions = this.getRenderedCornerPositions();
        this.rerenderImage();
        this._cesiumViewer.viewer.scene.screenSpaceCameraController.enableRotate = true;
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
        this._cesiumViewer.viewer.scene.requestRender();
        this._callback?.onProgress && this._callback?.onProgress("Operation Ended");
    }
    onMouseMoveToRotate(leftDownPosition, e) {
        try {
            const center = BoundingSphere.fromPoints(this._onEditInfo.wgs84CornerPositions).center;
            const center2D = this._cesiumViewer.viewer.scene.cartesianToCanvasCoordinates(center);
            const [angle, isClockwise] = calculateRotationAngleAndDirection(center2D, leftDownPosition, e.endPosition);
            const modelMatrix = createRotationModelMatrix(center, angle, isClockwise);
            this._imageUnderEdit.modelMatrix = modelMatrix;
            this.updateEditHelpers();
            this._cesiumViewer.viewer.scene.requestRender();
        }
        catch (err) {
            console.warn("rotate err");
        }
        this._callback?.onProgress && this._callback?.onProgress("Rotating");
    }
    onMouseMoveToTranslate(leftDownPosition, e) {
        try {
            const startRay = this._cesiumViewer.viewerCamera.getPickRay(leftDownPosition);
            const start = IntersectionTests.rayPlane(startRay, this._supportPlane);
            const endRay = this._cesiumViewer.viewerCamera.getPickRay(e.endPosition);
            const end = IntersectionTests.rayPlane(endRay, this._supportPlane);
            if (!end || !start)
                return;
            const diff = Cartesian3.subtract(end, start, new Cartesian3());
            if (Cartesian3.magnitude(diff) === 0)
                return;
            const newCornerPositions = translatePoints(this._onEditInfo.wgs84CornerPositions, diff);
            const oldWgs84Center = BoundingSphere.fromPoints(this._onEditInfo.wgs84CornerPositions).center;
            const newWgs84Center = BoundingSphere.fromPoints(newCornerPositions).center;
            const modelMatrix = createTranslationModelMatrix(oldWgs84Center, newWgs84Center);
            this._imageUnderEdit.modelMatrix = modelMatrix;
            this.updateEditHelpers();
            this._cesiumViewer.viewer.scene.requestRender();
        }
        catch (err) {
            console.warn("translate err");
        }
        this._callback?.onProgress && this._callback?.onProgress("Translating");
    }
    async onMouseMoveToScale(e) {
        try {
            const center = BoundingSphere.fromPoints(this._onEditInfo.wgs84CornerPositions).center;
            const center2D = this._cesiumViewer.viewer.scene.cartesianToCanvasCoordinates(center);
            const referencePoint = this._onEditInfo.wgs84CornerPositions[1];
            const referencePoint2D = this._cesiumViewer.viewer.scene.cartesianToCanvasCoordinates(referencePoint);
            const scaleFactor = calculateScaleFactor(center2D, referencePoint2D, e.endPosition);
            const modelMatrix = createScaleModelMatrix(center, scaleFactor);
            this._imageUnderEdit.modelMatrix = modelMatrix;
            this.updateEditHelpers();
            this._cesiumViewer.viewer.scene.requestRender();
        }
        catch (err) {
            console.warn("scale err");
        }
        this._callback?.onProgress && this._callback?.onProgress("Scaling");
    }
}
__decorate([
    isEditable()
], Drawing2DImageEditHandler.prototype, "end", null);
__decorate([
    isEditable()
], Drawing2DImageEditHandler.prototype, "cancel", null);
//# sourceMappingURL=drawing-2d-edit-handler.js.map